import { atom, useAtom } from 'jotai'
import qs from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useUserState } from '~/atoms/UserState'
import { useWebSocketService } from '~/hooks/useWebSocketService'
import { useMessageDraft } from '~/pages/Messaging/hooks/useMessageDraft'
import { usePendingMessages } from '~/pages/Messaging/hooks/usePendingMessages'
import { useGetPbxLineExtensions } from '~/queries/extensionQueries'
import {
  useGetThreadMessages,
  useSendMessage,
} from '~/queries/messagingQueries'
import EmitterService from '~/services/EventEmitter/EmitterService'
import { MessagingEmitterEventType } from '~/services/EventEmitter/types/EmitterTypeDefs'
import { PostMessageParams, WebSocketEventType } from '~/types'

import { Message, PaginatedApiResult } from '@mangovoice/types'

const THREAD_REFETCH_INTERVAL_SECONDS = 30 * 1000
const THREAD_CACHE_TIME_SECONDS = 15 * 1000
const PBX_EXTENSIONS_CACHE_TIME_SECONDS = 60 * 1000 * 5 // 5 minutes
const MESSAGE_FETCH_LIMIT = 25

interface ThreadMessagesState {
  threadMessages: Message[]
}

const threadMessagesStateAtom = atom<ThreadMessagesState>({
  threadMessages: [],
})

export const useThreadMessagesViewModel = (threadId: number) => {
  const [threadMessagesState, setThreadMessagesState] = useAtom(
    threadMessagesStateAtom,
  )
  const {
    pendingMessages,
    getPendingMessage,
    setPendingMessage,
    removePendingMessages,
  } = usePendingMessages({ threadId })
  const [messageText, setMessageText] = useMessageDraft(threadId)
  const [nextPageUri, setNextPageUri] = useState<string | null>()
  const { pbxId } = useUserState()
  const threadMessagesUpdatedCallback =
    useRef<(data: PaginatedApiResult<Message>) => void>()

  const { refetch: getThreadMessages, isFetching: isThreadFetching } =
    useGetThreadMessages({
      threadId,
      onQuerySuccess: (data) => {
        const results = data.results
        threadMessagesUpdatedCallback.current?.(data)

        const merged = [...results, ...threadMessagesState.threadMessages]

        setNextPageUri(data.next)
        setThreadMessagesState({
          threadMessages: merged,
        })
        // Remove pending messages that have been sent in case any are still present
        removePendingMessages(results)
      },
      options: {
        refetchOnWindowFocus: true,
        refetchInterval: THREAD_REFETCH_INTERVAL_SECONDS,
        gcTime: THREAD_CACHE_TIME_SECONDS,
      },
    })

  const { data: pbxExtensionsData } = useGetPbxLineExtensions({
    pbxId: pbxId ?? 0,
    options: {
      enabled: true,
      gcTime: PBX_EXTENSIONS_CACHE_TIME_SECONDS,
      staleTime: PBX_EXTENSIONS_CACHE_TIME_SECONDS,
    },
  })

  const { mutate: sendThreadMessageInternal } = useSendMessage({
    onSuccess: (response) => {
      if (response.postMessageParams.file) {
        window.gtag('event', 'mms_send_success', {
          thread_id: response.postMessageParams.threadId,
        })
      } else {
        window.gtag('event', 'sms_send_success', {
          thread_id: response.postMessageParams.threadId,
        })
      }

      const pendingMessage = getPendingMessage(
        response.postMessageParams.tempUuid,
      )

      if (pendingMessage) {
        setPendingMessage({
          ...pendingMessage,
          messageId: response.response.id,
        })
      }

      //fetch for new thread messages with offset
      getThreadMessages({
        offset: 0,
        limit: MESSAGE_FETCH_LIMIT,
      })
      console.log('finished sending the message!')

      //emit an event
      EmitterService.emit(MessagingEmitterEventType.THREAD_MESSAGE_SENT, {
        threadId,
      })
    },
    onError: (_, postMessageParams) => {
      const pendingMessage = getPendingMessage(postMessageParams.tempUuid)

      if (pendingMessage) {
        setPendingMessage({
          ...pendingMessage,
          status: 'failed',
        })
      }
    },
  })

  const sendThreadMessage = (
    postMessageParams: Omit<PostMessageParams, 'threadId'>,
  ) => {
    const postMessageParamsWithThreadId: PostMessageParams = {
      threadId,
      ...postMessageParams,
    }
    setPendingMessage({
      body: postMessageParams.body ?? postMessageParams.file?.name ?? 'Message',
      status: 'sending',
      uuid: postMessageParams.tempUuid,
      messageType: postMessageParams.file ? 'file' : 'text',
      created_at: new Date(),
      direction: 'outbound',
      originalSendParams: { ...postMessageParamsWithThreadId },
    })
    setMessageText('')
    sendThreadMessageInternal({ ...postMessageParamsWithThreadId })
  }

  const onThreadMessagesUpdated = (
    callback: (data: PaginatedApiResult<Message>) => void,
  ) => {
    threadMessagesUpdatedCallback.current = callback
  }

  // Subscribe to message received events for this thread
  const { subscribe: wssSubscribe } = useWebSocketService()
  wssSubscribe(WebSocketEventType.MessageReceived, {
    subscriptionToken: 'ThreadMessagesViewModel:MessageReceived',
    filter: (event) => event.payload.threadId === threadId,
    callback: (_) => {
      getThreadMessages({
        offset: 0,
        limit: MESSAGE_FETCH_LIMIT,
      })
    },
  })

  const tryGetPaginationParamsOrDefault = (queryString: string) => {
    const params = qs.parse(queryString)
    return {
      threadId: Number(params.thread_id),
      offset: Number(params.offset),
      search: String(params.search),
    }
  }

  const tryGetNextPage = () => {
    if (!nextPageUri) {
      return
    }
    const paginationParams = tryGetPaginationParamsOrDefault(nextPageUri)
    getThreadMessages(paginationParams)
  }

  useEffect(() => {
    getThreadMessages({
      offset: 0,
      limit: MESSAGE_FETCH_LIMIT,
    })
  }, [])

  return {
    threadMessagesState,
    pendingMessages,
    getThreadMessages,
    pbxExtensions: pbxExtensionsData?.results ?? [],
    isThreadFetching,
    onThreadMessagesUpdated,
    nextPageUri,
    tryGetNextPage,
    sendThreadMessage,
    messageText,
    setMessageText,
  }
}
